<template>
  <!-- Loading state -->
  <div v-if="isLoading" class="text-center py-12">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
    <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
  </div>

  <!-- Error state -->
  <div
    v-else-if="error"
    class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
  >
    <p class="text-red-800 dark:text-red-300">Fout bij laden van acties: {{ error.message }}</p>
  </div>

  <!-- Empty state -->
  <div v-else-if="actions.length === 0" class="text-center py-12">
    <div class="mx-auto h-16 w-16 text-gray-400">
      <ClipboardDocumentCheckIcon class="w-full h-full" />
    </div>
    <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">
      {{ emptyHeading }}
    </h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      {{ emptyDescription }}
    </p>
  </div>

  <!-- Actions List -->
  <template v-else>
    <!-- Mobile: single column -->
    <div class="lg:hidden space-y-4">
      <DoenActionCard
        v-for="action in actions"
        :key="action.id"
        :action="action"
        :expanded="!!expandedActions[action.id]"
        :updating="!!isUpdatingAction[action.id]"
        :is-admin="isAdmin"
        @toggle-expand="$emit('toggle-expand', $event)"
        @deal="$emit('deal', $event)"
        @no-deal="$emit('no-deal', $event)"
        @confirm-create-project="$emit('confirm-create-project', $event)"
        @pl-no-match="$emit('pl-no-match', $event)"
        @complete="$emit('complete', $event)"
        @delete="$emit('delete', $event)"
        @update-deadline="$emit('update-deadline', $event)"
      />
    </div>

    <!-- Desktop: 3 flex columns -->
    <div class="hidden lg:flex gap-4">
      <div
        v-for="(column, colIndex) in actionColumns"
        :key="colIndex"
        class="flex-1 flex flex-col gap-4"
      >
        <DoenActionCard
          v-for="action in column"
          :key="action.id"
          :action="action"
          :expanded="!!expandedActions[action.id]"
          :updating="!!isUpdatingAction[action.id]"
          :is-admin="isAdmin"
          @toggle-expand="$emit('toggle-expand', $event)"
          @deal="$emit('deal', $event)"
          @no-deal="$emit('no-deal', $event)"
          @confirm-create-project="$emit('confirm-create-project', $event)"
          @pl-no-match="$emit('pl-no-match', $event)"
          @complete="$emit('complete', $event)"
          @delete="$emit('delete', $event)"
          @update-deadline="$emit('update-deadline', $event)"
        />
      </div>
    </div>
  </template>
</template>

<script setup>
import { computed } from 'vue'
import { ClipboardDocumentCheckIcon } from '@heroicons/vue/24/outline'
import DoenActionCard from '@/components/doen/DoenActionCard.vue'

const props = defineProps({
  actions: { type: Array, required: true },
  isLoading: { type: Boolean, default: false },
  error: { type: Object, default: null },
  expandedActions: { type: Object, required: true },
  isUpdatingAction: { type: Object, required: true },
  isAdmin: { type: Boolean, default: false },
  emptyHeading: { type: String, default: 'Geen acties gevonden' },
  emptyDescription: { type: String, default: 'Er zijn momenteel geen acties.' }
})

defineEmits([
  'toggle-expand',
  'deal',
  'no-deal',
  'confirm-create-project',
  'pl-no-match',
  'complete',
  'delete',
  'update-deadline'
])

// Distribute actions across columns (horizontally: 1,2,3 then 4,5,6)
const columnCount = 3
const actionColumns = computed(() => {
  const columns = Array.from({ length: columnCount }, () => [])
  props.actions.forEach((action, index) => {
    const colIndex = index % columnCount
    columns[colIndex].push(action)
  })
  return columns
})
</script>
